<script lang="ts">
    
    import { navigate } from 'svelte-navigator';
    import { fly, fade } from 'svelte/transition';
    import type { Avatar, Feedback, Question as QuestionState, TrivialSource } from "../../global";
    import { avatar } from '../../Services/avatar';
    import Audio from '../Audio.svelte';
    import Clock from "../Clock.svelte";
    import Countdown from "../Countdown.svelte";
    import IconBtn from '../IconBtn.svelte';
    import LottiePlayer from '../LottiePlayer.svelte';
    import Music from '../Music.svelte';
    import Points from "../Points.svelte";
    import Question from "./Question.svelte";

    export let src: string;

    const gameTime = 90;

    let time = gameTime;
    let points = 0;
    let livesLeft = 3;
    let clock: Clock;
    let source: TrivialSource;
    let questions: QuestionState[];
    let showCountdown = false;
    let showGame = false;
    let showScore = false;
    let question: QuestionState | null = null;
    let themeFactor = 1;
    let shake = false;
    let feedback: Feedback;
    let winAudio: Audio;
    let failAudio: Audio;
    let clockMusic: Music;

    $: getSource(src);

    async function getSource(src: string): Promise<void> {
        try {
            const response = await fetch(src);
            if (!response.ok) {
                throw new Error('Ha ocurrido un error al descargar las preguntas del trivial.');
            }
            source = await response.json();
            showCountdown = true;
        } catch (error) {
            console.error(error);
        }
    }

    function onCountdownEnd(): void {
        startTrivial();
    }

    function startTrivial(): void {
        questions = [...source.questions[$avatar.questionsTheme]];
        questions.sort(() => -0.5 + Math.random());
        nextQuestion();
        clock.start();
        showCountdown = false;
        showGame = true;
        clockMusic?.play();
    }

    function resetGame(): void {
        //Cambiamos la variable time 2 veces para provocar
        //un cambio detectable por svelte
        time = 0;
        time = gameTime;
        points = 0;
        livesLeft = 3;
    }

    function nextQuestion(): void {
        if (questions.length === 0) {
            //No quedan preguntas
            //Terminamos el juego
            //Sumamos los segundos restantes como puntos
            points = points + clock.getTimeLeft();
            endGame();
            return;
        }
        question = questions.shift();
    }

    async function onWin(): Promise<void> {
        winAudio?.play();
        await waitMiliseconds(600);
        points = points + themeFactor * 10;
        nextQuestion();
    }

    async function onFail(): Promise<void> {
        failAudio?.play();
        shake = true;
        await waitMiliseconds(1500);

        livesLeft = livesLeft - 1;

        if (livesLeft === 0) {
            //No quedan vidas
            //El juego ha terminado
            await endGame();
            return;
        }
        
        nextQuestion();
    }

    async function endGame(): Promise<void> {
        question = null;
        clock.pause();
        showGame = false;
        clockMusic?.pause();
        await waitMiliseconds(600);

        //Seleccionamos el feedback apropiado
        const feedbackMessages = [...source.feedback];
        feedbackMessages.sort((a, b) => a.score > b.score ? -1 : 1);
        feedback = feedbackMessages.find(({ score }) => score <= points);

        showScore = true;
    } 

    function waitMiliseconds(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    function onTrivialAnimationEnd(): void {
        shake = false;
    }

    function onHome(): void {
        navigate('/');
    }

    function onReset(): void {
        resetGame();
        showCountdown = true;
    }

</script>

<style lang="scss">

    .trivial {
        position: relative;
        width: 100%;
        height: 100%;
        animation-duration: 600ms;
        animation-iteration-count: 1;
        animation-fill-mode: backwards;
        display: flex;
        flex-direction: column;

        &.shake {
            animation-name: error-shake;
        }
    }

    .header {
        display: flex;
        justify-content: space-between;
        padding: 1rem;
    }

    .game{
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .question {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 1rem;
    }

    .lives {
        height: 100px;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
    }

    .heart {
        $size: 60px;
        width: $size;
        height: $size;
        filter: grayscale(1);

        img {
            width: 100%;
            height: 100%;
        }
        
        &.available {
            filter: grayscale(0);
        }
    }

    .score {
        flex: 1 1 0;
        flex-direction: column;
        min-height: 0;
        display: flex;
    }

    .avatar {
        flex: 1 1 0;
        min-height: 0;
    }

    .feedback {
        color: #fff;
        font-size: 1.5rem;
        text-align: center;
        padding: 1rem 3rem;
    }

    .buttons {
        padding: 1rem;
        display: flex;
        justify-content: space-between;
    }

    @keyframes error-shake {
        0% { transform: translate(1px, 1px) rotate(0deg); }
        10% { transform: translate(-1px, -2px) rotate(-1deg); }
        20% { transform: translate(-3px, 0px) rotate(1deg); }
        30% { transform: translate(3px, 2px) rotate(0deg); }
        40% { transform: translate(1px, -1px) rotate(1deg); }
        50% { transform: translate(-1px, 2px) rotate(-1deg); }
        60% { transform: translate(-3px, 1px) rotate(0deg); }
        70% { transform: translate(3px, 1px) rotate(-1deg); }
        80% { transform: translate(-1px, -1px) rotate(1deg); }
        90% { transform: translate(1px, 2px) rotate(0deg); }
        100% { transform: translate(1px, -2px) rotate(-1deg); }
    }

</style>

<Audio bind:this={winAudio} src="/sound/win.mp3" volume={0.2} />
<Audio bind:this={failAudio} src="/sound/fail.mp3" volume={0.2} />
<Music bind:this={clockMusic} src="/sound/clock.mp3" volume={0.12} />

<div class="trivial" transition:fade class:shake on:animationend={onTrivialAnimationEnd}>
    <div class="header">
        <Clock {time} bind:this={clock} on:end={endGame} />
        <Points {points} />
    </div>

    {#if showCountdown}
    <Countdown on:end={onCountdownEnd} />
    {/if}

    {#if showGame}
    <div class="game">
        <div class="question">
            <Question {question} on:win={onWin} on:fail={onFail} />
        </div>
        <div class="lives">
            {#each [0, 1, 2] as number}
            <div class="heart" transition:fly={{ y: 100, delay: number * 100 }} class:available={livesLeft >= (number + 1)}>
                <img class="shadow" src="/images/heart.svg" alt="❤️">
            </div>
            {/each}
        </div>
    </div>
    {/if}

    {#if showScore}
    <div class="score">
        <div class="avatar" in:fade>
            <LottiePlayer src={$avatar.lottieSrc} />
        </div>
        <div class="feedback">{feedback.message}</div>
        <div class="buttons" in:fly={{ y: 200 }}>
            <IconBtn icon="/images/home.svg" text="Menu" on:click={onHome} />
            <IconBtn icon="/images/reload.svg" text="Volver" on:click={onReset} />
        </div>
    </div>
    {/if}

</div>
